import { buildClusterFaqJsonLdAsync } from "@/lib/clusterFaq";
import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { ServicePageEnhancer } from "@/components/service/ServicePageEnhancer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import AnswerBoxTop from "@/components/AnswerBoxTop";
import { HubCountryLinks } from "@/components/HubCountryLinks";
// /embassy — hub listing all embassy/consular legalization countries
import { createFileRoute, Link } from "@tanstack/react-router";
import { DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { VoiceSearchFAQ } from "@/components/seo/VoiceSearchFAQ";
import { speakableSchema, serviceWithReviewsSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { EMBASSY_COUNTRIES, EMBASSY_REGIONS } from "@/data/embassies";
import { consolidateGraph } from "@/lib/schema-graph";
import { Building2, ArrowRight, ShieldCheck } from "lucide-react";
import { TrustLayer } from "@/components/TrustLayer";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/embassy/")({
  loader: async () => ({
    clusterFaq: await buildClusterFaqJsonLdAsync("embassy", "th", "https://nyclanguageinstitute.com/embassy"),
    voiceFaqs: (await import("@/data/voice-faqs")).EMBASSY_VOICE_FAQS,
  }),
  head: ({ loaderData }: any) => {
    const url = `${SITE}/embassy`;
    const title = `รับรองเอกสารสถานทูต ${EMBASSY_COUNTRIES.length}+ ประเทศ · Apostille + Consular Legalization | NYC`;
    const desc = `บริการรับรองเอกสารสถานทูตและกงสุลครบ ${EMBASSY_COUNTRIES.length}+ ประเทศ: USA · UK · Japan · China · Australia · UAE · เยอรมัน ฝรั่งเศส และอีกมากมาย รวม Apostille MFA Thailand เสร็จ 5-14 วันทำการ — ปรึกษาฟรี ทีมงานตอบไวทุกวัน`;
    return {
      meta: [
        { title: ctaTitle(title, "/embassy", "th") },
        { name: "description", content: ctaDesc(desc, "/embassy", "th")},
        { property: "og:title", content: ctaTitle(title, "/embassy", "th")},
        { property: "og:description", content: ctaDesc(desc, "/embassy", "th")},
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/embassy")],
      scripts: [speakableSchema(),
        howToSchema({
          name: "วิธีรับรองเอกสารกับสถานทูตในประเทศไทย",
          description: "ขั้นตอนครบของการรับรองเอกสารกับสถานทูต/กงสุล โดย NYC Legal & Notary Service",
          inLanguage: "th-TH",
          totalTime: "P7D",
          estimatedCost: { currency: "THB", value: "2500" },
          steps: [
            { name: "ส่งเอกสารและประเมินราคา", text: "ส่งไฟล์เอกสารทาง LINE @nycli ทีมงานประเมินค่าใช้จ่ายและระยะเวลาให้ภายใน 30 นาที" },
            { name: "แปลและรับรองโดยนักแปลขึ้นทะเบียน", text: "นักแปลของ NYC แปลและออกใบรับรองคำแปลภายใน 1-2 วันทำการ" },
            { name: "รับรองกระทรวงการต่างประเทศ (MFA)", text: "ยื่นรับรองนิติกรณ์เอกสาร (Legalization) ที่กรมการกงสุล แจ้งวัฒนะ โดยทั่วไป 2-4 วันทำการ — Apostille จะเริ่มใช้กับเอกสารไทยเมื่ออนุสัญญามีผลบังคับใช้ในวันที่ 28 กุมภาพันธ์ 2570 (ที่มา: HCCH)" },
            { name: "ยื่นที่สถานทูต/กงสุลปลายทาง", text: "นำเอกสารยื่นที่สถานทูตปลายทางเพื่อรับรองขั้นสุดท้าย 1-7 วันทำการ" },
            { name: "ส่งคืนเอกสารต้นฉบับ", text: "รับเองที่สำนักงาน หรือจัดส่ง EMS / Grab ทั่วประเทศไทย พร้อมเลขติดตาม" },
          ],
        }),
        faqPageSchema([
          { q: "การรับรองสถานทูตในประเทศไทยใช้เวลานานเท่าใด?", a: "โดยทั่วไป 5-14 วันทำการ ขึ้นอยู่กับประเทศปลายทางและประเภทเอกสาร — บางสถานทูต (เช่น USA, Australia, UK, Canada) มีบริการ Express 1-3 วันทำการ มีค่าธรรมเนียมเพิ่ม สถานทูตประเทศอาหรับ (UAE, Saudi Arabia, Qatar) ใช้เวลา 10-21 วัน เนื่องจากต้องผ่าน Department of Foreign Affairs ของประเทศปลายทางเพิ่มเติม" },
          { q: "ราคารับรองสถานทูตเริ่มต้นเท่าไหร่?", a: "ค่าบริการ NYC เริ่ม 2,500 บาทต่อฉบับ (ยังไม่รวมค่ารับรอง MFA Thailand 400-800 บาทต่อฉบับ และค่าธรรมเนียมสถานทูตปลายทางตามจริง 800-5,000 บาท ขึ้นอยู่กับประเทศ) — ราคารวมการแปลโดย Sworn Translator, Notary Public, นัดคิว, รับ-ส่งเอกสาร และจัดส่งกลับให้ลูกค้าผ่าน EMS/DHL" },
          { q: "ต้องแปลเอกสารก่อนรับรองสถานทูตหรือไม่?", a: "ใช่ ต้องแปลเป็นภาษาราชการของประเทศปลายทาง (ส่วนใหญ่คือภาษาอังกฤษ บางประเทศต้องการภาษาท้องถิ่น เช่น อาหรับ จีน ญี่ปุ่น เกาหลี) โดยนักแปลขึ้นทะเบียน Sworn Translator ที่สถานทูตและศาลไทยรับรอง และต้องผ่านการรับรอง Notary Public + กระทรวงการต่างประเทศ (MFA) ก่อนยื่นสถานทูต" },
          { q: "Apostille กับ Consular Legalization ต่างกันอย่างไร?", a: "Apostille กับ Consular Legalization ต่างกันที่ Apostille ใช้สำหรับประเทศภาคีอนุสัญญากรุงเฮกโดยไม่ต้องผ่านสถานทูต ส่วน Consular Legalization ต้องรับรองผ่านกรมการกงสุลและสถานทูต โดยไทยจะเริ่มใช้ Apostille วันที่ 28 กุมภาพันธ์ 2570 ส่วนประเทศนอกภาคี เช่น จีน UAE เวียดนาม ยังต้องผ่านสถานทูตตามเดิม" },
          { q: "NYC ดำเนินการให้ครบทุกขั้นตอนได้หรือไม่?", a: "ได้ครบ End-to-End ตั้งแต่แปลภาษาโดย Sworn Translator → รับรองโดยทนาย Notary Public → รับรอง MFA Thailand (กรมการกงสุล แจ้งวัฒนะ) → ยื่นที่สถานทูตปลายทางในกรุงเทพ → จัดส่งกลับให้ลูกค้าผ่าน EMS/DHL ลูกค้าไม่ต้องเดินทางเอง มี Case Manager ติดตามทุกขั้น สอบถาม LINE @nycli, 083-249-4999" },
        ]),
        consolidateGraph([
          serviceWithReviewsSchema({ path: "/embassy", serviceType: "Embassy and Consular Legalization", name: title, description: desc, priceFromThb: 2500 }),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "@id": `${url}#page`,
              name: title,
              description: desc,
              hasPart: EMBASSY_COUNTRIES.map((c) => ({
                "@type": "WebPage",
                name: `รับรองสถานทูต${c.nameTh}`,
                url: `${SITE}/embassy/${c.slug}`,
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "รับรองสถานทูต", item: url },
              ],
            }),
          },
        ]),
      
        ...(loaderData?.clusterFaq ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.clusterFaq) }] : []),
      ],
    };
  },
  component: EmbassyHub,
});

function EmbassyHub() {
  const { voiceFaqs } = Route.useLoaderData();
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-navy-deep text-white">
        <div className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Building2 className="h-3.5 w-3.5" /> Consular Legalization · Apostille
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            รับรองเอกสารสถานทูต {EMBASSY_COUNTRIES.length}+ ประเทศ
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            ครบทุกขั้นตอน: นักแปลรับรอง → MFA Thailand (Apostille / Consular) → สถานทูต/กงสุลปลายทาง — เสร็จ 5–14 วันทำการ มีบริการ Express
          </p>
          <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr_1fr]">
            <DirectAnswer entity="NYC Embassy Legalization Thailand">
              NYC Language Institute ดำเนินการรับรองเอกสารที่สถานทูตและกงสุลครบ {EMBASSY_COUNTRIES.length}+ ประเทศ — แปลรับรองโดยนักแปล MoJ → กรมการกงสุล กระทรวงต่างประเทศ (MFA แจ้งวัฒนะ) → ยื่นสถานทูตปลายทางในกรุงเทพฯ (จีน, อินเดีย, UAE, ซาอุดิอาระเบีย, เวียดนาม, อียิปต์ ฯลฯ) เสร็จ 5–14 วันทำการ มีบริการ Express 2–3 วัน
            </DirectAnswer>
            <KeyFactsTable rows={[
              { k: "ครอบคลุม", v: `${EMBASSY_COUNTRIES.length}+ สถานทูต` },
              { k: "ระยะเวลา", v: "5–14 วันทำการ" },
              { k: "Express", v: "2–3 วันทำการ" },
              { k: "ขั้นตอน", v: "แปล → MFA → สถานทูต" },
              { k: "เอกสาร", v: "ทะเบียน · การศึกษา · พาณิชย์" },
              { k: "พื้นที่บริการ", v: "ทั่วประเทศ · ส่งทางไปรษณีย์" },
            ]} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8 sm:mt-10">
        <VoiceSearchFAQ topic="Embassy Legalization Thailand" faqs={voiceFaqs} entity="NYC Embassy Legalization Thailand" />
      </section>

      <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
        {EMBASSY_REGIONS.map((region) => {
          const list = EMBASSY_COUNTRIES.filter((c) => c.region === region.id);
          if (!list.length) return null;
          return (
            <section key={region.id} className="mb-12">
              <h2 className="font-display text-2xl font-extrabold text-navy-deep">{region.label}</h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {list.map((c) => (
                  <Link
                    key={c.slug}
                    to="/embassy/$country"
                    params={{ country: c.slug }}
                    className="group flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4 transition hover:-translate-y-0.5 hover:border-gold/50 hover:shadow-md"
                  >
                    <div className="text-3xl leading-none">{c.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-base font-extrabold text-navy-deep group-hover:text-amber-600">
                        {c.nameTh}
                      </div>
                      <div className="mt-0.5 text-[11px] text-slate-500">{c.nameEn}</div>
                      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-600">
                        <ShieldCheck className="h-3 w-3 text-emerald-600" />
                        เริ่ม ฿{c.serviceFeeTh.toLocaleString()} · {c.turnaround}
                      </div>
                    </div>
                    <ArrowRight className="mt-1 h-4 w-4 text-slate-400 group-hover:text-amber-600" />
                  </Link>
                ))}
              </div>
            </section>
          );
        })}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 mb-12">
        <h2 className="font-display text-2xl font-extrabold text-navy-deep">เอกสารที่ต้องรับรองกรมการกงสุลก่อนเข้าสถานทูต</h2>
        <p className="mt-2 text-sm leading-7 text-slate-600">
          ทุกฉบับที่จะยื่นสถานทูตต้องผ่านการรับรองนิติกรณ์ที่กรมการกงสุล กระทรวงการต่างประเทศ ก่อนเสมอ
          อ่านลำดับขั้นตอนและเช็กลิสต์การเตรียมเอกสารแยกตามกลุ่มเอกสารได้ที่คู่มือด้านล่าง
          — ข้อมูลอ้างอิงกรมการกงสุล (consular.mfa.go.th) และ HCCH (อนุสัญญา Apostille มีผลกับไทย 28 ก.พ. 2570)
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to="/mfa-legalisation/documents" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy-deep hover:border-amber-400">
            คู่มือเอกสารรับรองกงสุล 6 กลุ่ม →
          </Link>
          <Link to="/notary/documents" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-navy-deep hover:border-amber-400">
            เอกสารที่ทนาย Notary รับรองได้ →
          </Link>
        </div>
      </section>

      <RelatedHubGrid locale="th" />
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="embassy" locale="th" variant="light" />
          <HubCountryLinks service="embassy" lang="th" />
      <ServicePageEnhancer serviceName="รับรองสถานทูต" locale="th" />

      <PlanningToolsLinks locale="th" />
      </main>
  );
}
